<template>
  <div
    tabindex="0"
    @keydown="preventSelectText"
    @keyup="allowSelectText">
    <v-app
      class="main-app"
      :class="[$route.params.fieldName, $route.name, offSelectedText]">
      <AppLoadingRoute v-if="isLoadingRoute" />
      <PromiseDialogsWrapper />
      <CreateProjectModal />
      <ProjectDetailInfoModal />
      <ShareModal />
      <SuggestDialogSnackbar />

      <!-- This modal popups for new users -->
      <!-- to show tour video -->
      <TourModal v-if="shouldShowTutorialVideo" />

      <!-- Sizes your content based upon application components -->
      <v-main :class="[$route.name, computedMargins]">
        <!-- Left Navigation Bar -->
        <NavBar v-if="isNotLoginRelatedPage && userInfo && isNotProject" />

        <!-- Top Navigation Bar -->
        <AppHeader
          v-if="isNotLoginRelatedPage && isNotProject"
          :user-info="userInfo" />

        <AppBannerManagePayment v-if="!useRemoveUpgradeNotice" />
        <transition
          name="page"
          mode="out-in">
          <router-view />
        </transition>
      </v-main>

      <Spinner />
      <SnackBar />
      <ProjectDetailsExportSnackBar />
      <MaintenanceBanner
        v-if="maintenanceMode && showMaintenanceBanner"
        @close="showMaintenanceBanner = false" />
      <AppBanner
        v-if="showMainBanner && paymentStatus"
        :massage="paymentStatus"
        @close="showMainBanner = false" />
      <AppBanner
        v-if="banner.value"
        :massage="banner.message"
        @close="closeBanner" />

      <!-- preview files / images in carousel -->
      <FilePreview />

      <AppPDFEditor
        v-if="pdfEditorContext"
        :src="pdfEditorContext.src"
        :attachment-id="pdfEditorContext.attachmentId"
        :storage-key="pdfEditorContext.storageKey"
        :filename="pdfEditorContext.filename"
        @close="closePdfEditor" />

      <!-- Right bottom AI Chat (or Contact Us if locked/Free WS -->
      <div
        v-if="isNotLoginRelatedPage && notQuickLinkPage && !isPaymentPage"
        class="app-btm-right-container">
        <AppSallyAI v-if="showSallyAI" />
        <AppAIChat v-else />
      </div>
      <div
        v-if="isPaymentPage"
        class="app-btm-right-container-no-middle">
        <v-hover v-slot="{ hover }">
          <v-btn
            class="app-contact-us-btn"
            style="position: relative; bottom:1.2rem; right: 1.2rem"
            :color="hover ? 'red' : 'black'"
            @click="contactSupport">
            {{ 'Contact Us' }}
          </v-btn>
        </v-hover>
      </div>
    </v-app>
  </div>
</template>
<script>
import {
  mapActions, mapState, mapGetters, mapMutations,
} from 'vuex';
import { PromiseDialogsWrapper } from 'vue-promise-dialogs';
import { WORKSPACE_PERMISSION } from '@/constants/userPermissions';
// components
import AppSallyAI from '@/components/App/AppSallyAI';
import AppAIChat from '@/components/App/AppAIChat';
import AppBanner from '@/components/App/AppBanner';
import AppLoadingRoute from '@/components/App/AppLoadingRoute';
import CreateProjectModal from '@/components/Projects/ProjectsCreateProjectModal';
import FilePreview from '@/components/App/AppFilePreview';
import MaintenanceBanner from '@/components/App/AppMaintenanceBanner';
import NavBar from '@/components/App/AppNavBar';
import ProjectDetailInfoModal from '@/components/App/AppProjectDetailInfoModal';
import ProjectDetailsExportSnackBar
  from '@/components/ProjectDetails/ProjectDetailsExportTo/ProjectDetailsExportSnackBar';
import ShareModal from '@/components/App/AppShareModal';
import SnackBar from '@/components/App/AppSnackBar';
import Spinner from '@/components/App/AppLoadingSpinner';
import TourModal from '@/components/App/TourModal';
import AppPDFEditor from '@/components/App/AppPDFEditor';
// mixins
import OfflineDetection from '@/mixins/OfflineDetection';
import ConfirmStayInDirtyForm from '@/mixins/confirmLeaveWithUnsavedData';
import { INFO_EMAIL } from '@/constants/urls';
import { TYPE_WORKSPACE_FREE } from '@/constants';
import {
  EventBus,
  OPEN_PDF_EDITOR,
} from '@/utils/eventBus';
export default {
  name: 'App',
  components: {
    AppSallyAI,
    AppAIChat,
    AppBanner,
    AppLoadingRoute,
    CreateProjectModal,
    FilePreview,
    MaintenanceBanner,
    NavBar,
    ProjectDetailInfoModal,
    ProjectDetailsExportSnackBar,
    PromiseDialogsWrapper,
    ShareModal,
    SnackBar,
    Spinner,
    TourModal,
    AppPDFEditor,
    AppBannerManagePayment: () => import('@/components/App/AppBanner/AppBannerManagePayment'),
    AppHeader: () => import('@/components/App/AppHeader'),
    SuggestDialogSnackbar: () => import('@/components/App/AppSuggestDialog/SuggestDialogSnackbar'),
  },
  mixins: [
    OfflineDetection,
    ConfirmStayInDirtyForm,
  ],
  data() {
    return {
      drawer: false,
      showMaintenanceBanner: true,
      showMainBanner: true,
      fileRemoveCounter: 0,
      isSelectTextOnPage: true,
      pdfEditorContext: null,
    };
  },
  computed: {
    ...mapState(['userInfo', 'loginRelatedPages', 'banner', 'isLoadingRoute', 'viewTutorial']),
    ...mapState('ManageFiles', ['cachedFiles']),
    ...mapState('Workspace', ['activeWorkspaceId']),
    ...mapGetters('FeatureFlags', [
      'maintenanceMode',
      'useRemoveUpgradeNotice',
      'useMakePageFollowable',
      'showSallyAI',
    ]),
    ...mapGetters('UserProfile', ['isUnsuccessfulPayment']),
    ...mapGetters('Workspace', ['getActiveWorkspace', 'getActiveWorkspaceType']),
    computedMargins() {
      if (this.isNotProject && this.isNotLoginRelatedPage && this.$route.name !== 'community-collections') {
        return this.$vuetify.breakpoint.smAndDown ? 'mb-32' : 'mb-25';
      }
      return '';
    },
    isNotLoginRelatedPage() {
      const name = this.$route?.name;
      return name && !this.loginRelatedPages.includes(name);
    },
    isNotProject() {
      return !(this.$vuetify.breakpoint.lgAndUp && ['schedule-library', 'project-library'].includes(this.$route.name));
    },
    notQuickLinkPage() {
      return !(['project-library', 'shared-collection'].includes(this.$route.name));
    },
    offSelectedText() {
      return !this.isSelectTextOnPage ? 'select-page-none' : '';
    },
    paymentStatus() {
      const status = this.getActiveWorkspace?.status === 'paidFailed';
      const type = this.getActiveWorkspace?.type === 'premiumExpired';
      if (status || type) {
        return 'You are in the Read-Only mode as the last payment was unsuccessful. Please check your payment method to be able to use all the premium features again.';
      } else if (this.isUnsuccessfulPayment) {
        const group = this.premiumGroup ? 'Premium' : 'Collaborator';
        return `The last payment was unsuccessful. Please check your payment method to be able to use all the ${group} features again.`;
      }
      return false;
    },
    isPaymentPage() {
      return this.$route.name === 'workspace-payment';
    },
    shouldShowTutorialVideo() {
      return this.viewTutorial
        && !this.$vuetify.breakpoint.smAndDown
        && this.getActiveWorkspaceType !== TYPE_WORKSPACE_FREE;
    },
  },
  watch: {
    activeWorkspaceId(val) {
      if (!val) {
        this.setStaringFilteredViews([]);
        return;
      }
      this.subscribeWorkspacePermissionChange();
      this.getStarredProjectList();
      if (this.useMakePageFollowable) {
        this.subscribeFollowedWorkspacePage();
      }
    },
    cachedFiles(val) {
      if (!val.length || this.fileRemoveCounter > 0) {
        return;
      }
      this.fileRemoveCounter++;
      this.removeFileIfTimeIsNotLeft();
    },
  },
  async created() {
    this.setsStoreIsMobile();
    this.subscribeToScheduleExport();
    EventBus.$on(OPEN_PDF_EDITOR, this.openPdfEditorFromEvent);
  },
  beforeDestroy() {
    this.removeSubscriptions([WORKSPACE_PERMISSION]);
    EventBus.$off(OPEN_PDF_EDITOR, this.openPdfEditorFromEvent);
  },
  methods: {
    ...mapActions({
      subscribeToListOfExportScheduleFiles: 'ExportSchedule/subscribeToListOfExportScheduleFiles',
      subscribeToListOfFilesToNotify: 'ExportSchedule/subscribeToListOfFilesToNotify',
      subscribeWorkspacePermissionChange: 'UserPermissions/subscribeWorkspacePermissionChange',
      removeSubscriptions: 'ManageSubscriptions/removeSubscriptions',
      removeFileIfTimeIsNotLeft: 'ManageFiles/removeFileIfTimeIsNotLeft',
      subscribeFollowedWorkspacePage: 'MyPages/subscribeFollowedWorkspacePage',
      getStarredProjectList: 'Projects/getStarredProjectList',
    }),
    ...mapMutations({
      closeBanner: 'closeBanner',
      setIsMobile: 'setIsMobile',
      setStaringFilteredViews: 'Projects/setStaringFilteredViews',
    }),
    subscribeToScheduleExport() {
      this.subscribeToListOfExportScheduleFiles();
      this.subscribeToListOfFilesToNotify();
    },
    setsStoreIsMobile() {
      const isMobile = Boolean(this.$mobileDetect.mobile()) || Boolean(this.$mobileDetect.tablet());
      this.setIsMobile(isMobile);
    },
    preventSelectText(e) {
      if (e.shiftKey && !e.repeat) this.isSelectTextOnPage = false;
    },
    allowSelectText(e) {
      if (e.key === 'Shift') this.isSelectTextOnPage = true;
    },
    contactSupport() {
      window.location.href = `mailto:${INFO_EMAIL}`;
    },
    closePdfEditor() {
      this.pdfEditorContext = null;
    },
    async openPdfEditorFromEvent(rawCtx) {
      if (this.pdfEditorContext) {
        // Ignore open requests while editor is already open.
        console.info('[App] OPEN_PDF_EDITOR ignored (editor already open)', {
          currentAttachmentId: this.pdfEditorContext?.attachmentId,
          currentSource: this.pdfEditorContext?.source,
        });
        return;
      }
      const ctx = rawCtx && typeof rawCtx === 'object' ? rawCtx : null;
      if (!ctx) return;

      if (!['filePreview', 'rowExpandedView', 'productDialog'].includes(ctx.source)) {
        console.warn('[App] OPEN_PDF_EDITOR invalid source');
        return;
      }

      const attachmentId = (ctx.attachmentId || '').toString().trim();
      const storageKey = (ctx.storageKey || '').toString().trim();
      const filename = (ctx.filename || '').toString().trim();
      let src = (ctx.src || '').toString().trim();

      if (!attachmentId && !filename) {
        console.warn('[App] OPEN_PDF_EDITOR missing file information');
        return;
      }
      if (!storageKey && !src) {
        console.warn('[App] OPEN_PDF_EDITOR missing storageKey/src');
        return;
      }

      if (!src) {
        console.warn('[App] OPEN_PDF_EDITOR could not resolve src');
        return;
      }

      this.pdfEditorContext = {
        src,
        attachmentId,
        storageKey: storageKey || undefined,
        filename,
        source: ctx.source,
      };
    },
  },
};
</script>
<style lang="scss" src="./App.scss"></style>
